import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context/NavigationAnalyticsContext';
import { useAccountLinks } from '@atlassian/account/hooks/use-account-links';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { testIdGenerate } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
import {
	getIdentityEnv,
	getContinueUrl,
} from '@jira/navigation-apps__atlassian-navigation-nav4-common/src/common/utils.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { ANALYTICS_CONTEXT } from '@atlassian/account-menu/analytics';
import { AccountMenuIntlProvider } from '@atlassian/account-menu/account-menu-intl-provider';
import { AccountMenuLoggedOutState } from '@atlassian/account-menu/nav4/account-menu-logged-out-state';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';

import { LOGIN } from '@jira/navigation-apps__atlassian-navigation-nav4-common/src/common/constants.tsx';
import type { ProfileNew$key } from './__generated__/ProfileNew.graphql';
import { AccountMenu } from './account-menu/AccountMenu.tsx';

type Nav4ProfileProps = {
	queryReference: ProfileNew$key;
};

export function ProfileNew({ queryReference }: Nav4ProfileProps): React.JSX.Element {
	const isAnonymous = useIsAnonymous();

	return (
		<NavigationAnalyticsContext data={ANALYTICS_CONTEXT}>
			<AccountMenuIntlProvider>
				{isAnonymous ? <ProfileAnonymous /> : <ProfileLoggedIn queryReference={queryReference} />}
			</AccountMenuIntlProvider>
		</NavigationAnalyticsContext>
	);
}

function ProfileLoggedIn({ queryReference }: Nav4ProfileProps) {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<ProfileNew$key>(
		graphql`
			fragment ProfileNew on Query {
				user(accountId: $atlassianAccountId) {
					picture
					... on AtlassianAccountUser {
						email
					}
				}
				...AccountMenu_triggerQuery
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryReference,
	);

	return (
		<AccountMenu
			testId={testIdGenerate('secondary-actions', 'profile')}
			picture={data?.user?.picture}
			email={data?.user?.email ?? ''}
			accountMenuTriggerQueryFragment={data}
		/>
	);
}

function ProfileAnonymous() {
	const environment = useEnvironment();
	const identityEnv = getIdentityEnv(environment);
	const orgId = useOrgId();

	const links = useAccountLinks({
		email: '',
		identityEnvironment: identityEnv,
		// The following application is for Org controlled Social Login Customization
		// https://hello.atlassian.net/wiki/spaces/CloudSecurity/blog/2025/05/22/5335913476/Login+Customisations+is+Live#%3Aknowledge-base%3A--Using-Advanced-Cobranding-For-Frontend
		application: 'admin--jira-atlas-75650',
		continueUrl: getContinueUrl({ type: LOGIN }),
		isLoggedOut: true,
		logInLinkCustomParameters: {
			// orgId query param is required for Social Login Customization to work
			orgId: orgId || '',
		},
	});

	return (
		links?.logInLink && (
			<AccountMenuLoggedOutState
				testId={testIdGenerate('secondary-actions', 'profile')}
				logInLink={links.logInLink}
			/>
		)
	);
}
